import React, { useState } from 'react';
import { MapPin } from 'lucide-react';

export default function GeographicAnalysisPanel({ data }) {
  const [sortBy, setSortBy] = useState('revenue'); // 'revenue', 'orders', 'quantity'

  if (!data || data?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Analyse Géographique</h3>
        <div className="text-center py-8 text-gray-500">
          <MapPin size={48} className="mx-auto mb-2 text-gray-300" />
          <p>Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  const sortedData = [...data]?.sort((a, b) => {
    switch (sortBy) {
      case 'orders':
        return b?.orderCount - a?.orderCount;
      case 'quantity':
        return (b?.noirQuantity + b?.bleuQuantity) - (a?.noirQuantity + a?.bleuQuantity);
      default:
        return b?.totalRevenue - a?.totalRevenue;
    }
  });

  const maxRevenue = Math.max(...data?.map(d => d?.totalRevenue));
  const totalRevenue = data?.reduce((sum, d) => sum + d?.totalRevenue, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin size={20} className="text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Analyse Géographique</h3>
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e?.target?.value)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="revenue">Par Revenu</option>
          <option value="orders">Par Commandes</option>
          <option value="quantity">Par Quantité</option>
        </select>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                Emplacement
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                Commandes
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                Quantité
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                Revenu
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                Part
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedData?.map((location, index) => {
              const totalQuantity = location?.noirQuantity + location?.bleuQuantity;
              const revenueShare = (location?.totalRevenue / totalRevenue) * 100;
              
              return (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-gray-400" />
                      <span className="font-medium text-gray-900">{location?.location}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium text-gray-900">{location?.orderCount}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-gray-600">N:{location?.noirQuantity}</span>
                      <span className="text-xs text-gray-600">B:{location?.bleuQuantity}</span>
                      <span className="font-medium text-gray-900">{totalQuantity}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-semibold text-gray-900">{location?.totalRevenue?.toFixed(2)} €</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${(location?.totalRevenue / maxRevenue) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium text-gray-600">{revenueShare?.toFixed(1)}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-gray-600 mb-1">Total Emplacements</p>
          <p className="text-lg font-bold text-gray-900">{data?.length}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600 mb-1">Revenu Total</p>
          <p className="text-lg font-bold text-green-600">{totalRevenue?.toFixed(2)} €</p>
        </div>
        <div>
          <p className="text-xs text-gray-600 mb-1">Top Emplacement</p>
          <p className="text-lg font-bold text-gray-900">{sortedData?.[0]?.location}</p>
        </div>
      </div>
    </div>
  );
}